import React, { useEffect, useState } from 'react';
import { Link } from '@reach/router';
import { prefersReducedMotion } from './useCountUp';

export default function StatCard({
    title,
    icon: Icon,
    iconClassName = 'bg-accent-soft text-orange-500',
    headline,
    headlineClassName = 'text-foreground',
    subtitle,
    url,
    visible,
    index = 0,
    children,
}: {
    title: string;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    iconClassName?: string;
    headline: React.ReactNode;
    headlineClassName?: string;
    subtitle?: React.ReactNode;
    url: string;
    visible: boolean;
    index?: number;
    children?: React.ReactNode;
}) {
    const [mounted, setMounted] = useState(prefersReducedMotion());
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        if (prefersReducedMotion()) return undefined;
        const timer = setTimeout(() => setMounted(true), index * 60);
        return () => clearTimeout(timer);
    }, [index]);

    if (!visible) return null;

    let translateY = '8px';
    if (mounted) translateY = hovered ? '-2px' : '0';

    return (
        <Link
            to={url}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="block bg-surface border border-border rounded-xl p-5 flex flex-col gap-4 no-underline"
            style={{
                opacity: mounted ? 1 : 0,
                transform: `translateY(${translateY})`,
                borderColor: hovered ? 'var(--color-border-strong)' : 'var(--color-border)',
                boxShadow: hovered
                    ? '0 8px 20px -8px rgba(0,0,0,0.18)'
                    : '0 1px 2px rgba(0,0,0,0.02)',
                transition:
                    'opacity 400ms ease-out, transform 200ms ease-out, border-color 200ms ease, box-shadow 200ms ease',
            }}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                    <span
                        className={`flex items-center justify-center w-9 h-9 rounded-lg ${iconClassName}`}
                    >
                        <Icon className="w-5 h-5" />
                    </span>
                    <span className="text-sm font-semibold text-foreground">{title}</span>
                </div>
                <svg
                    className="w-4 h-4 text-foreground-muted flex-shrink-0 mt-0.5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    style={{
                        opacity: hovered ? 1 : 0.4,
                        transform: hovered ? 'translateX(2px)' : 'translateX(0)',
                        transition: 'opacity 200ms ease, transform 200ms ease',
                    }}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                    />
                </svg>
            </div>

            <div>
                <div className={`text-3xl font-bold leading-none ${headlineClassName}`}>
                    {headline}
                </div>
                {subtitle && (
                    <div className="text-xs text-foreground-secondary mt-1.5">{subtitle}</div>
                )}
            </div>

            {children}
        </Link>
    );
}
